import React, { useEffect, Children } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, PhoneIcon, ShieldCheckIcon, AlertCircleIcon, FileTextIcon, ArrowRightIcon, HeartHandshakeIcon, MessageSquareIcon, StarIcon } from 'lucide-react';
import { LegalFooter } from '../components/LegalFooter';
export function ThankYouPage() {
  const location = useLocation();
  const fromSubmission = location.state?.fromSubmission;
  useEffect(() => {
    // Fire ViewContent event when thank you page loads
    if (typeof window !== 'undefined' && window.fbq && fromSubmission) {
      window.fbq('track', 'ViewContent', {
        content_name: 'Thank You Page'
      });
    }
    // Scroll to top on load
    window.scrollTo(0, 0);
  }, [fromSubmission]);
  // Protect route: redirect to home if not from form submission
  if (!fromSubmission) {
    return <Navigate to="/" replace data-id="element-135" />;
  }
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24
      }
    }
  };
  return <div className="min-h-screen bg-navy flex flex-col relative" data-id="element-136">
      <header className="bg-navy-light border-b border-navy-lighter py-3 px-4" data-id="element-137">
        <div className="max-w-7xl mx-auto flex justify-center items-center" data-id="element-138">
          <img src="https://cdn.magicpatterns.com/uploads/opXSaFkmappgfGitP8aDsi/logo-text_-_Edited.png" alt="The Founders Law" className="h-7 md:h-9 w-auto" data-id="element-139" />
        </div>
      </header>

      <main className="flex-1 pb-16" data-id="element-140">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-4xl mx-auto px-4 pt-8 md:pt-12" data-id="element-141">
          {/* 1. HERO SECTION */}
          <motion.section variants={itemVariants} className="text-center pt-8 md:pt-12 pb-8 md:pb-12 mb-16 md:mb-20" data-id="element-142">
            <motion.div className="inline-flex items-center justify-center w-20 h-20 bg-green/10 rounded-full mb-6" initial={{
            scale: 0,
            rotate: -180
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.3
          }} data-id="element-143">
              <motion.div animate={{
              boxShadow: ['0 0 0 0px rgba(34,197,94,0.4)', '0 0 0 16px rgba(34,197,94,0)']
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatDelay: 1
            }} className="inline-flex items-center justify-center w-20 h-20 rounded-full" data-id="element-144">
                <motion.div initial={{
                pathLength: 0,
                opacity: 0
              }} animate={{
                pathLength: 1,
                opacity: 1
              }} transition={{
                delay: 0.8,
                duration: 0.4
              }} data-id="element-145">
                  <CheckCircleIcon className="w-10 h-10 text-green" data-id="element-146" />
                </motion.div>
              </motion.div>
            </motion.div>
            <h1 className="text-3xl md:text-5xl font-black text-white mb-6 leading-tight" data-id="element-147">
              ¡Su solicitud ha sido enviada con éxito!
            </h1>
            <div className="space-y-2 mb-8" data-id="element-148">
              <p className="text-gray-400 text-lg font-medium" data-id="element-149">
                En los próximos 5 minutos recibirá un mensaje confirmando que
                recibimos su información.
              </p>
              <p className="text-gray-400 text-lg" data-id="element-150">
                Un miembro de nuestro equipo le llamará para hablar sobre su
                caso y los próximos pasos.
              </p>
            </div>
            <a href="sms:+17866274479?body=Hola, ya completé el formulario y estoy listo para recibir la llamada de un asesor. Gracias." className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-5 md:py-4 px-8 rounded-xl transition-colors shadow-lg shadow-blue-600/20" data-id="element-151">
              <MessageSquareIcon className="w-6 h-6 md:w-5 md:h-5" data-id="element-152" />
              Confirmar vía Texto
            </a>
          </motion.section>

          {/* 2. URGENCY SECTION (Paso 1) */}
          <motion.section variants={itemVariants} className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 mb-12 md:mb-16" data-id="element-153">
            <div className="text-center mb-8" data-id="element-154">
              <span className="inline-block bg-orange/20 text-orange text-xs font-bold px-3 py-1 rounded-full mb-4 tracking-wider" data-id="element-155">
                • PASO 1 — ANTES DE SU LLAMADA
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" data-id="element-156">
                Tenga esta información a la mano
              </h2>
              <p className="text-gray-400" data-id="element-157">
                Cuanta más información tenga lista, más rápido podremos evaluar
                su caso.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-158">
              {[{
              title: 'Información del incidente',
              desc: 'Fecha, lugar y descripción detallada de lo ocurrido.'
            }, {
              title: 'Reporte de Policía',
              desc: 'Número de reporte si fue generado en la escena.'
            }, {
              title: 'Información del Seguro',
              desc: 'Su póliza y la del tercero involucrado.'
            }, {
              title: 'Reporte de Incidente',
              desc: 'Si ocurrió en el trabajo o en una propiedad privada.'
            }].map((item, i) => <div key={i} className="flex gap-4 bg-navy-light p-5 rounded-xl border border-white/5" data-id="element-159">
                  <CheckCircleIcon className="w-6 h-6 text-green flex-shrink-0 mt-0.5" data-id="element-160" />
                  <div data-id="element-161">
                    <h3 className="text-white font-bold mb-1" data-id="element-162">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed" data-id="element-163">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </motion.section>

          {/* 3. PROCESS SECTION (Paso 2) */}
          <motion.section variants={itemVariants} className="pt-8 md:pt-12 pb-8 md:pb-12 mb-14 md:mb-16" data-id="element-164">
            <div className="text-center mb-8" data-id="element-165">
              <span className="inline-block bg-orange/20 text-orange text-xs font-bold px-3 py-1 rounded-full mb-4 tracking-wider" data-id="element-166">
                • PASO 2 — PREPÁRESE
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" data-id="element-167">
                Cómo estar preparado para su llamada
              </h2>
              <p className="text-gray-400" data-id="element-168">
                Esto nos permite ir directo al grano y darle una evaluación
                honesta de su caso.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-169">
              {[{
              num: '1',
              title: 'Piense en los detalles',
              desc: '¿Qué pasó exactamente? ¿Quién estaba presente? ¿Hubo testigos?'
            }, {
              num: '2',
              title: 'Calcule sus pérdidas',
              desc: 'Gastos médicos, días de trabajo perdidos, daños a su propiedad.'
            }, {
              num: '3',
              title: 'Tenga preguntas listas',
              desc: 'Esta llamada es para usted. Aproveche para resolver todas sus dudas.'
            }].map((step, i) => <div key={i} className="flex flex-col items-center text-center bg-navy p-6 rounded-2xl border border-white/5" data-id="element-170">
                  <div className="w-12 h-12 rounded-full bg-orange/10 border-2 border-orange flex items-center justify-center mb-4 text-orange font-black text-xl" data-id="element-171">
                    {step.num}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-172">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-173">
                    {step.desc}
                  </p>
                </div>)}
            </div>
          </motion.section>

          {/* 4. TRUST SECTION */}
          <motion.section variants={itemVariants} className="mb-16 md:mb-20" data-id="element-174">
            <div className="bg-gradient-to-b from-green/5 to-navy border border-green/20 rounded-2xl p-8 text-center shadow-2xl" data-id="element-175">
              <ShieldCheckIcon className="w-10 h-10 md:w-16 md:h-16 text-green mx-auto mb-6" data-id="element-176" />
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-177">
                Su caso merece atención real
              </h2>
              <p className="text-gray-300 max-w-2xl mx-auto mb-8 text-lg leading-relaxed" data-id="element-178">
                Nuestro equipo está disponible para escucharle y orientarle.
                Llámenos ahora para hablar directamente con un asesor — sin
                compromiso.
              </p>
              <a href="tel:+17866274479" onClick={() => {
              if (window.fbq) window.fbq('track', 'Contact');
            }} className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-green hover:bg-green-hover text-white font-black py-6 md:py-4 px-8 md:px-10 rounded-xl transition-all transform hover:scale-105 shadow-lg hover:shadow-green/20 text-xl md:text-xl" data-id="element-179">
                <PhoneIcon className="w-8 h-8 md:w-6 md:h-6 fill-current" data-id="element-180" />
                Llame Ahora: (786) 627-4479
              </a>
            </div>
          </motion.section>

          {/* 5. NEW TESTIMONIALS SECTION */}
          <motion.section variants={itemVariants} className="mb-20" data-id="element-181">
            <div className="text-center mb-8" data-id="element-182">
              <span className="inline-block bg-orange/20 text-orange text-xs font-bold px-3 py-1 rounded-full mb-4 tracking-wider" data-id="element-183">
                • TESTIMONIOS
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6" data-id="element-184">
                Clientes reales. Resultados reales.
              </h2>

              <div className="flex flex-wrap justify-center gap-3 mb-10" data-id="element-185">
                {[{
                name: 'Google',
                rating: '4.9'
              }, {
                name: 'Facebook',
                rating: '5.0'
              }, {
                name: 'Yelp',
                rating: '4.8'
              }].map((platform, i) => <div key={i} className="flex items-center gap-2 bg-navy-light border border-white/10 px-4 py-2 rounded-full" data-id="element-186">
                    <span className="text-white font-bold text-sm" data-id="element-187">
                      {platform.name}
                    </span>
                    <div className="flex items-center gap-1" data-id="element-188">
                      <StarIcon className="w-4 h-4 text-yellow-400 fill-current" data-id="element-189" />
                      <span className="text-yellow-400 font-bold text-sm" data-id="element-190">
                        {platform.rating}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-191">
              {['https://www.instagram.com/reel/DU3d59dDa5R/embed/', 'https://www.instagram.com/reel/DTimk97iULK/embed/', 'https://www.instagram.com/reel/DQ4XumdAMjW/embed/'].map((link, i) => <div key={i} className="aspect-[9/16] md:aspect-[9/14] bg-navy-light rounded-2xl border border-white/5 relative overflow-hidden shadow-lg" data-id="element-192">
                  <iframe src={link} width="100%" height="100%" frameBorder="0" scrolling="no" allowTransparency={true} allow="encrypted-media" className="absolute inset-0 w-full h-full" data-id="element-193" />
                </div>)}
            </div>
          </motion.section>

          {/* 6. FINAL CTA SECTION */}
        </motion.div>
      </main>

      <LegalFooter data-id="element-194" />
    </div>;
}